import { formatDate } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { lookupViaCity } from 'city-timezones';
import { firstValueFrom, Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { Game, GameStatus, IdType, SeasonType } from '../../domain/game';
import { League } from '../../domain/league';
import { Team } from '../../domain/team';
import { AlertService } from '../../services/alert.service';
import { GameService } from '../../services/game.service';
const DEFAULT_UTC_OFFSET = '+00:00';

const PRIORITY_TIMEZONES: readonly string[] = [
  'Europe/Zurich',
  'Europe/Berlin',
  'Europe/London',
  'Europe/Helsinki',
  'America/New_York',
  'America/Toronto',
  'America/Regina',
  'America/Vancouver',
  'Asia/Hong_Kong',
  'Pacific/Auckland',
  'Atlantic/Reykjavik'
];

interface TimezoneOption {
  readonly timezoneId: string;
  readonly label: string;
  readonly offset: string;
}

@Component({
  selector: 'app-game-details-card-essentials',
  templateUrl: './game-details-essentials-card.component.html',
  styleUrls: ['./game-details-essentials-card.component.css']
})
export class GameDetailsEssentialsCardComponent implements OnInit, OnChanges {
  @Input() game: Game = new Game({});
  @Input() gameUnmodified!: Game;
  @Input() saving = false;
  readonly seasonTypes = Object.values(SeasonType);
  readonly idTypes = Object.values(IdType);
  readonly currentDate = new Date();
  readonly usersLocalTimezoneShortName = this.currentDate
    .toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'short' })
    .substring(4);
  timezoneControl = new FormControl<string>('');
  filteredTimezones$: Observable<TimezoneOption[]> = of([]);
  venueLocalDate: string | null = null;
  applyingScheduleCorrection = false;
  private allTimezoneOptions: string[] = [];
  private timezoneOffsetMap = new Map<string, string>();

  @Input() seasons: number[] = [];
  @Input() leagues: League[] = [];
  @Input() teams: Team[] = [];

  @Output() seasonChange = new EventEmitter<string>();
  @Output() idTypeChange = new EventEmitter<string>();
  @Output() leagueChange = new EventEmitter<string>();
  @Output() homeTeamChange = new EventEmitter<Team | null>();
  @Output() awayTeamChange = new EventEmitter<Team | null>();
  @Output() essentialsChange = new EventEmitter<void>();

  constructor(
    private gameService: GameService,
    private alertService: AlertService
  ) {}

  get teamChangeDisabled() {
    return [
      GameStatus.IN_COLLECTION,
      GameStatus.IN_EXTENDED_COLLECTION
    ].includes(this.game.status);
  }

  get correctionDateDisplay(): string | null {
    const correctionDate = this.game.pendingScheduleCorrection?.correctionDate;
    if (!correctionDate) {
      return null;
    }
    return formatDate(
      correctionDate,
      'yyyy-MM-dd HH:mm',
      'en-US',
      this.game.venueUTCOffset ?? DEFAULT_UTC_OFFSET
    );
  }

  async applyScheduleCorrection() {
    if (!this.game.pendingScheduleCorrection || !this.game._id) {
      return;
    }
    this.applyingScheduleCorrection = true;
    try {
      const updatedGame = await firstValueFrom(
        this.gameService.applyScheduleCorrection(this.game._id)
      );
      this.game.date = updatedGame.date;
      this.game.venueName = updatedGame.venueName;
      this.game.pendingScheduleCorrection = undefined;
      if (this.gameUnmodified) {
        this.gameUnmodified.date = updatedGame.date;
        this.gameUnmodified.venueName = updatedGame.venueName;
        this.gameUnmodified.pendingScheduleCorrection = undefined;
      }
      this.setupVenueFields();
      this.alertService.showInfo('Schedule correction applied');
    } catch (e) {
      this.alertService.showError(
        'Could not apply schedule correction: ' + e.message
      );
    } finally {
      this.applyingScheduleCorrection = false;
    }
  }

  ngOnInit() {
    this.setupVenueFields();
    if (!this.game._id) {
      this.onVenueTimezoneChange(this.game.venueTimezoneId);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['game'] && !changes['game'].firstChange) {
      this.setupVenueFields();
      if (!this.game._id) {
        this.onVenueTimezoneChange(this.game.venueTimezoneId);
      }
    }
  }

  get usersLocalDatePreview(): string | null {
    if (!this.venueLocalDate || !this.game.venueUTCOffset) {
      return null;
    }
    const isoWithOffset = `${this.venueLocalDate}:00${this.game.venueUTCOffset}`;
    const utcDate = new Date(isoWithOffset);
    if (isNaN(utcDate.getTime())) {
      return null;
    }
    return formatDate(utcDate, 'yyyy-MM-dd HH:mm', 'en-US');
  }

  validateAndSyncVenueData() {
    this.checkVenueTimezone();
    this.checkVenueDate();
    this.convertLocalDateToUTC();
  }

  onTimezoneSelected(timezoneId: string) {
    this.game.venueTimezoneId = timezoneId;
    this.onVenueTimezoneChange(timezoneId);
    this.emitEssentialsChange();
  }

  showTimezoneSuggestions() {
    if (!this.timezoneControl.value) {
      this.timezoneControl.setValue('');
    }
  }

  onVenueDateChange(dateString: string) {
    this.venueLocalDate = dateString || null;
    this.emitEssentialsChange();
  }

  onEssentialsFieldChange() {
    this.emitEssentialsChange();
  }

  private setupVenueFields() {
    this.allTimezoneOptions = (
      Intl as unknown as { supportedValuesOf(key: string): string[] }
    ).supportedValuesOf('timeZone');
    this.timezoneControl.setValue(this.game.venueTimezoneId ?? '', {
      emitEvent: false
    });
    if (this.game.date) {
      this.venueLocalDate = formatDate(
        this.game.date,
        'yyyy-MM-ddTHH:mm',
        'en-US',
        this.game.venueUTCOffset ?? DEFAULT_UTC_OFFSET
      );
    }
    this.precomputeOffsets();
    this.filteredTimezones$ = this.timezoneControl.valueChanges.pipe(
      startWith(''),
      map((query) => this.buildTimezoneOptions(query ?? ''))
    );
  }

  private precomputeOffsets() {
    const refDate = this.venueLocalDate
      ? new Date(this.venueLocalDate)
      : new Date();
    this.timezoneOffsetMap.clear();
    for (const tz of this.allTimezoneOptions) {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'longOffset'
      });
      const parts = formatter.formatToParts(refDate);
      const offsetStr =
        parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
      const offset =
        offsetStr.match(/GMT([+-]\d{2}:\d{2})/)?.[1] ?? DEFAULT_UTC_OFFSET;
      this.timezoneOffsetMap.set(tz, offset);
    }
  }

  private formatTimezoneLabel(tz: string): string {
    const offset = this.timezoneOffsetMap.get(tz) ?? DEFAULT_UTC_OFFSET;
    return `${tz} (UTC${offset})`;
  }

  private buildTimezoneOptions(query: string): TimezoneOption[] {
    if (!query) {
      return this.buildDefaultTimezoneOptions();
    }

    const lower = query.toLowerCase();
    const seenTimezones = new Set<string>();
    const allSet = new Set(this.allTimezoneOptions);

    const ianaMatches = this.matchByIanaId(lower, seenTimezones);
    const cityMatches = this.matchByCityName(query, seenTimezones, allSet);
    const offsetMatches = this.matchByOffset(lower, seenTimezones);

    return [...cityMatches, ...ianaMatches, ...offsetMatches];
  }

  private buildDefaultTimezoneOptions(): TimezoneOption[] {
    return PRIORITY_TIMEZONES.filter((tz) =>
      this.allTimezoneOptions.includes(tz)
    ).map((tz) => this.toTimezoneOption(tz));
  }

  private matchByIanaId(lower: string, seen: Set<string>): TimezoneOption[] {
    return this.allTimezoneOptions
      .filter((tz) => tz.toLowerCase().includes(lower))
      .map((tz) => {
        seen.add(tz);
        return this.toTimezoneOption(tz);
      });
  }

  private matchByCityName(
    query: string,
    seen: Set<string>,
    allowedSet: Set<string>
  ): TimezoneOption[] {
    const matches: TimezoneOption[] = [];
    for (const city of lookupViaCity(query)) {
      if (!seen.has(city.timezone) && allowedSet.has(city.timezone)) {
        seen.add(city.timezone);
        const offset =
          this.timezoneOffsetMap.get(city.timezone) ?? DEFAULT_UTC_OFFSET;
        matches.push({
          timezoneId: city.timezone,
          label: `${city.city}, ${city.country} — ${city.timezone} (UTC${offset})`,
          offset
        });
      }
    }
    return matches;
  }

  private matchByOffset(lower: string, seen: Set<string>): TimezoneOption[] {
    const stripped = lower.startsWith('utc') ? lower.slice(3) : lower;
    if (!/^[+-]\d/.test(stripped)) {
      return [];
    }
    const matches: TimezoneOption[] = [];
    for (const tz of this.allTimezoneOptions) {
      if (seen.has(tz)) {
        continue;
      }
      const offset = this.timezoneOffsetMap.get(tz) ?? DEFAULT_UTC_OFFSET;
      if (offset.startsWith(stripped)) {
        seen.add(tz);
        matches.push(this.toTimezoneOption(tz));
      }
    }
    return matches;
  }

  private toTimezoneOption(tz: string): TimezoneOption {
    return {
      timezoneId: tz,
      label: this.formatTimezoneLabel(tz),
      offset: this.timezoneOffsetMap.get(tz) ?? DEFAULT_UTC_OFFSET
    };
  }

  private onVenueTimezoneChange(timezoneId: string | undefined) {
    if (!timezoneId) {
      this.game.venueUTCOffset = null;
      return;
    }
    this.game.venueUTCOffset = this.calculateUTCOffset(timezoneId);
  }

  private calculateUTCOffset(timezoneId: string): string {
    const gameDate = this.venueLocalDate
      ? new Date(this.venueLocalDate)
      : new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneId,
      timeZoneName: 'longOffset'
    });
    const parts = formatter.formatToParts(gameDate);
    const offsetStr = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    return offsetStr.match(/GMT([+-]\d{2}:\d{2})/)?.[1] ?? DEFAULT_UTC_OFFSET;
  }

  private checkVenueDate() {
    if (!this.venueLocalDate) {
      throw new Error('Venue date is required');
    }
  }

  private checkVenueTimezone() {
    const isLegacyGame =
      this.gameUnmodified?.date && !this.gameUnmodified?.venueTimezoneId;
    if (!this.game.venueTimezoneId && !isLegacyGame) {
      throw new Error('Venue timezone is required');
    }
  }

  private convertLocalDateToUTC() {
    if (this.venueLocalDate) {
      const offset = this.game.venueUTCOffset ?? DEFAULT_UTC_OFFSET;
      const isoWithOffset = `${this.venueLocalDate}:00${offset}`;
      this.game.date = new Date(isoWithOffset).toISOString();
    } else {
      this.game.date = null;
    }
  }

  private emitEssentialsChange() {
    this.essentialsChange.emit();
  }
}
